/*
 * The security center (draft 11), for owners and franchise owners: every login here and below with its roles, two-step
 * and devices, the access review every 90 days, and one activity log across every company below. Every list can act:
 * sign a login out on one device or all of them, remove access, sign out everyone at once (the panic button), and sign
 * a client out of the client hub. Logins that are not owners sign out on their own after 90 days with no use.
 */
import { useState, type MouseEvent } from "react";
import type { ActivityRow, HubSignIn, SecurityLogin } from "@steward/shared";
import {
  useActivity,
  useHubSignIns,
  useHubSignOut,
  useMarkReviewed,
  useRemoveAccess,
  useSecurity,
  useSignOutDevice,
  useSignOutEveryone,
  useSignOutLogin,
  useTerritories,
} from "../api/hq";
import { useSession } from "../app/session";
import { DataTable } from "../components/DataTable";
import { Combobox } from "../components/fields";
import { shortDate } from "../components/format";
import { Modal, useToast } from "../components/overlays";
import { ago, Banner, Button, Card, Details, LoadError, Page, ROLE_WORDS, Skeleton, StatusLabel } from "../components/parts";
import { TWO_STEP_WORDS } from "./HeadOffice";

const GROUP_WORDS = { owners: "Owners", office: "Office", crew: "Crew" } as const;
const ACTION_WORDS: Record<string, string> = {
  "auth.sign_in": "Signed in",
  "auth.sign_in_failed": "Wrong password typed",
  "auth.two_step_on": "Turned on two-step",
  "people.invite": "Sent an invite",
  "people.update": "Changed a person",
  "people.role": "Gave a role",
  "people.role_end": "Took a role away",
  "people.login_off": "Turned a login off",
  "people.login_on": "Turned a login back on",
  "security.reviewed": "Marked the access review done",
  "security.sign_out": "Signed someone out everywhere",
  "security.sign_out_device": "Signed someone out on one device",
  "security.sign_out_all": "Signed out everyone",
  "security.hub_sign_out": "Signed a client out of the client hub",
  "security.remove_access": "Removed someone's access",
  "security.idle_sign_out": "Signed out after 90 days with no use",
  "franchise.plan": "Changed the franchise plan",
  "franchise.card": "Saved the card for the fee",
  "franchise.bill_paid": "Fee paid",
  "franchise.bill_failed": "Card turned down for the fee",
  "territories.update": "Changed ZIP codes",
  "settings.update": "Changed settings",
  "keys.save": "Saved a service key",
  "company.create": "Added a company",
  "company.update": "Changed business info",
};
/** Activity worth a second look, shown in warning color. */
const ALERTS = new Set(["auth.sign_in_failed", "security.remove_access", "security.sign_out_all"]);
const errText = (e: unknown) => (e instanceof Error ? e.message : "That didn't work.");
/** A button inside a clickable row: it does its own thing, not open the row. */
const own = (fn: () => void) => (e: MouseEvent) => {
  e.stopPropagation();
  fn();
};
const devicesWord = (n: number) => `${String(n)} ${n === 1 ? "device" : "devices"}`;

type Tab = "logins" | "hub" | "activity";

export function Security() {
  const { company } = useSession();
  const [tab, setTab] = useState<Tab>("logins");
  const [panic, setPanic] = useState(false);
  return (
    <Page
      title="Security"
      subtitle={company.name}
      secondaryAction={
        <Button type="secondary" variation="destructive" onClick={() => setPanic(true)}>
          Sign out everyone
        </Button>
      }
    >
      <div className="segmented page-tabs" role="tablist" aria-label="Security">
        {(
          [
            ["logins", "Logins and devices"],
            ["hub", "Client hub"],
            ["activity", "Activity"],
          ] as const
        ).map(([v, label]) => (
          <button key={v} type="button" role="tab" aria-selected={tab === v} onClick={() => setTab(v)}>
            {label}
          </button>
        ))}
      </div>
      {tab === "logins" ? <Logins /> : tab === "hub" ? <Hub /> : <Activity />}
      {panic ? <SignOutEveryone onClose={() => setPanic(false)} /> : null}
    </Page>
  );
}

function SignOutEveryone({ onClose }: { onClose: () => void }) {
  const all = useSignOutEveryone();
  const toast = useToast();
  const [err, setErr] = useState<string | null>(null);
  return (
    <Modal
      title="Sign out everyone?"
      open
      onClose={onClose}
      footer={
        <div className="row">
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variation="destructive"
            loading={all.isPending}
            onClick={() =>
              all.mutate(undefined, {
                onSuccess: (r) => {
                  toast(`Signed out ${String(r.logins)} ${r.logins === 1 ? "login" : "logins"} on ${devicesWord(r.devices)}`);
                  onClose();
                },
                onError: (e) => setErr(errText(e)),
              })
            }
          >
            Sign out everyone
          </Button>
        </div>
      }
    >
      <p>
        Use this if you think someone got in who should not. Every login here and below that is under you signs out on every
        device right now. You stay signed in.
      </p>
      <p className="muted">They sign back in with their password and two-step. To keep someone out, remove their access.</p>
      {err ? <Banner type="error">{err}</Banner> : null}
    </Modal>
  );
}

function Logins() {
  const { company } = useSession();
  const q = useSecurity(company.id);
  const review = useMarkReviewed();
  const out = useSignOutLogin();
  const toast = useToast();
  const [open, setOpen] = useState<{ id: string; remove: boolean } | null>(null);
  if (q.error) return <LoadError error={q.error} retry={() => void q.refetch()} />;
  const s = q.data;
  if (!s) return <Skeleton rows={6} />;
  const r = s.review;
  const notSetUp = s.logins.filter((l) => l.twoStep === "not_set_up").length;
  const current = s.logins.find((l) => l.userId === open?.id) ?? null;
  return (
    <>
      <Banner
        type={r.overdue ? "warning" : "success"}
        action={
          <Button
            type={r.overdue ? "primary" : "secondary"}
            loading={review.isPending}
            onClick={() => review.mutate(undefined, { onSuccess: () => toast("Review marked done. Next one in 90 days.") })}
          >
            Mark reviewed
          </Button>
        }
      >
        {r.lastAt
          ? `Access review last done ${shortDate(r.lastAt.slice(0, 10))}${r.byName ? ` by ${r.byName}` : ""}. ${r.overdue ? "It is due now." : `Next one by ${shortDate(r.dueOn)}.`}`
          : "Look over every login and device below, then mark the access review done. It is due every 90 days."}
      </Banner>
      {notSetUp ? (
        <Banner type="error">
          {notSetUp === 1 ? "One login needs" : `${String(notSetUp)} logins need`} two-step and has not set it up. They can&apos;t
          get in until they do.
        </Banner>
      ) : null}
      <DataTable
        title="Logins"
        rows={s.logins}
        rowKey={(l) => l.userId}
        onRowClick={(l) => setOpen({ id: l.userId, remove: false })}
        search={(l) => `${l.name} ${l.roles.map((x) => x.companyName).join(" ")}`}
        empty="Nobody signs in here yet."
        columns={[
          {
            key: "name",
            header: "Name",
            sort: (l) => l.name,
            render: (l) => (
              <div className="name-cell">
                <b>{l.name}</b>
                <span className="muted small">{GROUP_WORDS[l.group]}</span>
              </div>
            ),
          },
          {
            key: "roles",
            header: "Role",
            render: (l) => l.roles.map((x) => `${ROLE_WORDS[x.role] ?? x.role} at ${x.companyName}`).join(". "),
          },
          {
            key: "two",
            header: "Two-step",
            render: (l) => <StatusLabel status={TWO_STEP_WORDS[l.twoStep]![1]}>{TWO_STEP_WORDS[l.twoStep]![0]}</StatusLabel>,
          },
          {
            key: "last",
            header: "Last active",
            sort: (l) => l.lastActiveAt ?? "",
            render: (l) =>
              l.lastActiveAt ? (
                <span className={l.idleDays !== null && l.idleDays >= 60 ? "warning-text" : undefined}>
                  {ago(l.lastActiveAt)}
                </span>
              ) : (
                <span className="muted">Never</span>
              ),
          },
          { key: "dev", header: "Devices", align: "right", render: (l) => String(l.devices.length) },
          {
            key: "act",
            header: "",
            align: "right",
            render: (l) =>
              l.canAct ? (
                <div className="row nowrap">
                  <Button
                    type="secondary"
                    size="small"
                    disabled={l.devices.length === 0}
                    loading={out.isPending && out.variables === l.userId}
                    onClick={own(() =>
                      out.mutate(l.userId, {
                        onSuccess: (x) => toast(`${l.name} signed out on ${devicesWord(x.devices)}`),
                        onError: (e) => toast(errText(e)),
                      }),
                    )}
                  >
                    Sign out
                  </Button>
                  <Button
                    type="tertiary"
                    size="small"
                    variation="destructive"
                    onClick={own(() => setOpen({ id: l.userId, remove: true }))}
                  >
                    Remove
                  </Button>
                </div>
              ) : null,
          },
        ]}
      />
      <p className="muted small">Logins that are not owners sign out on their own after 90 days with no use.</p>
      {current ? <LoginSheet login={current} removing={open?.remove ?? false} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

function LoginSheet({ login, removing, onClose }: { login: SecurityLogin; removing?: boolean; onClose: () => void }) {
  const out = useSignOutLogin();
  const one = useSignOutDevice();
  const remove = useRemoveAccess();
  const toast = useToast();
  const [sure, setSure] = useState(removing ?? false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <Modal title={login.name} open onClose={onClose}>
      <Details
        rows={[
          ["Role", login.roles.map((x) => `${ROLE_WORDS[x.role] ?? x.role} at ${x.companyName}`).join(". ")],
          ["Two-step", TWO_STEP_WORDS[login.twoStep]![0]],
          ["Last active", login.lastActiveAt ? ago(login.lastActiveAt) : "Never"],
        ]}
      />
      <Card title="Devices signed in">
        <DataTable
          title="Devices"
          rows={login.devices}
          rowKey={(d) => d.id}
          empty="No devices signed in."
          columns={[
            { key: "d", header: "Device", render: (d) => d.device },
            { key: "ip", header: "Address", render: (d) => <span className="num muted">{d.ip ?? "Unknown"}</span> },
            { key: "since", header: "Signed in", render: (d) => ago(d.signedInAt) },
            { key: "used", header: "Last used", render: (d) => ago(d.lastUsedAt) },
            ...(login.canAct
              ? [
                  {
                    key: "act",
                    header: "",
                    align: "right" as const,
                    render: (d: SecurityLogin["devices"][number]) => (
                      <Button
                        type="tertiary"
                        size="small"
                        loading={one.isPending && one.variables === d.id}
                        onClick={() =>
                          one.mutate(d.id, {
                            onSuccess: () => toast(`Signed out on ${d.device}`),
                            onError: (e) => setErr(errText(e)),
                          })
                        }
                      >
                        Sign out
                      </Button>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Card>
      {err ? <Banner type="error">{err}</Banner> : null}
      {!login.canAct ? (
        <p className="muted">You can&apos;t sign out or remove yourself, or someone at your level or above.</p>
      ) : sure ? (
        <Banner
          type="warning"
          action={
            <div className="row">
              <Button type="tertiary" onClick={() => setSure(false)}>
                Cancel
              </Button>
              <Button
                variation="destructive"
                loading={remove.isPending}
                onClick={() =>
                  remove.mutate(login.userId, {
                    onSuccess: () => {
                      toast("Access removed");
                      onClose();
                    },
                    onError: (e) => setErr(errText(e)),
                  })
                }
              >
                Remove access
              </Button>
            </div>
          }
        >
          {login.name} loses every role here and below and signs out now. Their records stay for payroll and tax.
        </Banner>
      ) : (
        <div className="row">
          <Button
            type="secondary"
            disabled={login.devices.length === 0}
            loading={out.isPending}
            onClick={() =>
              out.mutate(login.userId, {
                onSuccess: (r) => toast(`Signed out on ${devicesWord(r.devices)}`),
                onError: (e) => setErr(errText(e)),
              })
            }
          >
            Sign out everywhere
          </Button>
          <Button type="tertiary" variation="destructive" onClick={() => setSure(true)}>
            Remove access
          </Button>
        </div>
      )}
    </Modal>
  );
}

const hubNames = (h: HubSignIn) => h.clients.map((c) => c.name).join(", ");

function Hub() {
  const { company } = useSession();
  const q = useHubSignIns(company.id);
  const out = useHubSignOut();
  const toast = useToast();
  const [open, setOpen] = useState<string | null>(null);
  const current = q.data?.find((h) => h.key === open) ?? null;
  return (
    <>
      <p className="muted">
        Clients signed in to the client hub. Signing a client out signs them out of every brand&apos;s hub. They get back in only
        with a code texted to their own phone.
      </p>
      <DataTable
        title="Client hub sign-ins"
        rows={q.data ?? []}
        rowKey={(h) => h.key}
        loading={q.isLoading}
        error={q.error ? <LoadError error={q.error} retry={() => void q.refetch()} /> : undefined}
        onRowClick={(h) => setOpen(h.key)}
        search={(h) => `${hubNames(h)} ${h.phoneEnd} ${h.clients.map((c) => c.companyName).join(" ")}`}
        empty="No clients are signed in to the client hub."
        columns={[
          {
            key: "who",
            header: "Client",
            sort: hubNames,
            render: (h) => (
              <div className="name-cell">
                <b>{hubNames(h)}</b>
                <span className="muted small">{[...new Set(h.clients.map((c) => c.companyName))].join(", ")}</span>
              </div>
            ),
          },
          {
            key: "phone",
            header: "Phone",
            render: (h) => <span className="num muted">{h.phoneEnd ? `Ends in ${h.phoneEnd}` : "Hidden"}</span>,
          },
          {
            key: "how",
            header: "Access",
            render: (h) =>
              h.devices.every((d) => d.lookOnly) ? (
                <StatusLabel status="inactive">Look only</StatusLabel>
              ) : (
                <StatusLabel status="success">Full</StatusLabel>
              ),
          },
          { key: "last", header: "Last seen", sort: (h) => h.lastSeenAt, render: (h) => ago(h.lastSeenAt) },
          { key: "dev", header: "Devices", align: "right", render: (h) => String(h.devices.length) },
          {
            key: "act",
            header: "",
            align: "right",
            render: (h) => (
              <Button
                type="secondary"
                size="small"
                loading={out.isPending && out.variables.sessionId === h.key}
                onClick={own(() =>
                  out.mutate(
                    { sessionId: h.key, all: true },
                    {
                      onSuccess: (x) => toast(`${hubNames(h)} signed out on ${devicesWord(x.devices)}`),
                      onError: (e) => toast(errText(e)),
                    },
                  ),
                )}
              >
                Sign out
              </Button>
            ),
          },
        ]}
      />
      {current ? <HubSheet hub={current} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

function HubSheet({ hub, onClose }: { hub: HubSignIn; onClose: () => void }) {
  const out = useHubSignOut();
  const toast = useToast();
  const [err, setErr] = useState<string | null>(null);
  const signOut = (sessionId: string, all: boolean) =>
    out.mutate(
      { sessionId, all },
      {
        onSuccess: (x) => {
          toast(`Signed out on ${devicesWord(x.devices)}`);
          if (all) onClose();
        },
        onError: (e) => setErr(errText(e)),
      },
    );
  return (
    <Modal title={hubNames(hub)} open onClose={onClose}>
      <Details
        rows={[
          ["Client at", hub.clients.map((c) => `${c.name} at ${c.companyName}`).join(". ")],
          ["Phone", hub.phoneEnd ? `Ends in ${hub.phoneEnd}` : "Hidden"],
          ["Last seen", ago(hub.lastSeenAt)],
        ]}
      />
      <Card title="Devices signed in">
        <DataTable
          title="Devices"
          rows={hub.devices}
          rowKey={(d) => d.id}
          empty="No devices signed in."
          columns={[
            {
              key: "d",
              header: "Device",
              render: (d) => (
                <div className="name-cell">
                  <span>{d.device}</span>
                  {d.lookOnly ? <span className="muted small">Opened from a link, look only</span> : null}
                </div>
              ),
            },
            { key: "ip", header: "Address", render: (d) => <span className="num muted">{d.ip ?? "Unknown"}</span> },
            { key: "since", header: "Signed in", render: (d) => ago(d.signedInAt) },
            { key: "used", header: "Last seen", render: (d) => ago(d.lastSeenAt) },
            {
              key: "act",
              header: "",
              align: "right",
              render: (d) => (
                <Button
                  type="tertiary"
                  size="small"
                  loading={out.isPending && out.variables.sessionId === d.id && !out.variables.all}
                  onClick={() => signOut(d.id, false)}
                >
                  Sign out
                </Button>
              ),
            },
          ]}
        />
      </Card>
      {err ? <Banner type="error">{err}</Banner> : null}
      <div className="row">
        <Button type="secondary" loading={out.isPending && out.variables.all} onClick={() => signOut(hub.key, true)}>
          Sign out everywhere
        </Button>
      </div>
    </Modal>
  );
}

/** The login an activity row is about: the person it was done to, or else the person who did it. */
const personOf = (a: ActivityRow) => (a.subjectType === "user" && a.subjectId ? a.subjectId : a.userId);

function Activity() {
  const { company } = useSession();
  const [filter, setFilter] = useState("");
  const showPicker = company.level !== "location";
  const places = useTerritories(company.id, showPicker);
  const q = useActivity(company.id, filter);
  const logins = useSecurity(company.id).data?.logins ?? [];
  const [open, setOpen] = useState<string | null>(null);
  const loginOf = (a: ActivityRow) => {
    const id = personOf(a);
    return id ? (logins.find((l) => l.userId === id) ?? null) : null;
  };
  const current = logins.find((l) => l.userId === open) ?? null;
  return (
    <>
      {showPicker ? (
        <Combobox
          label="Company"
          value={filter}
          onChange={setFilter}
          options={[
            { value: "", label: "Every company" },
            ...(places.data?.rows ?? []).map((p) => ({ value: p.companyId, label: p.name })),
          ]}
        />
      ) : null}
      <DataTable
        title="Activity"
        rows={q.data ?? []}
        rowKey={(a) => a.id}
        loading={q.isLoading}
        error={q.error ? <LoadError error={q.error} retry={() => void q.refetch()} /> : undefined}
        onRowClick={(a) => {
          const l = loginOf(a);
          if (l) setOpen(l.userId);
        }}
        search={(a) => `${a.userName ?? ""} ${ACTION_WORDS[a.action] ?? a.action} ${a.companyName}`}
        empty="Sign-ins, roles and changes show here."
        columns={[
          { key: "at", header: "When", sort: (a) => a.at, render: (a) => ago(a.at) },
          ...(showPicker ? [{ key: "c", header: "Company", render: (a: { companyName: string }) => a.companyName }] : []),
          { key: "who", header: "Who", render: (a) => a.userName ?? <span className="muted">The Steward</span> },
          {
            key: "what",
            header: "What",
            render: (a) => (
              <span className={ALERTS.has(a.action) ? "warning-text" : undefined}>{ACTION_WORDS[a.action] ?? a.action}</span>
            ),
          },
          { key: "ip", header: "Address", render: (a) => <span className="num muted">{a.ip ?? ""}</span> },
          {
            key: "act",
            header: "",
            align: "right",
            render: (a) => {
              const l = loginOf(a);
              return l?.canAct ? (
                <Button type="tertiary" size="small" onClick={own(() => setOpen(l.userId))}>
                  {a.action === "auth.sign_in_failed" ? "Check login" : "Manage"}
                </Button>
              ) : null;
            },
          },
        ]}
      />
      {current ? <LoginSheet login={current} onClose={() => setOpen(null)} /> : null}
    </>
  );
}
